
import { enqueueSnackbar } from 'notistack';
import React, { useMemo, useState } from 'react';

import { Box } from '@mui/system';
import {
  Card,
  Chip,
  Stack,
  Table,
  Button,
  Select,
  MenuItem,
  TableRow,
  useTheme,
  TableBody,
  TableCell,
  TableHead,
  CardHeader,
  IconButton,
  InputLabel,
  Typography,
  CardContent,
  FormControl,
  Box as MuiBox,
  useMediaQuery,
  TableContainer
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';
import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { useTranslate } from 'src/locales';
import { PageOptions } from 'src/services/api.types';
import { AccountRole } from 'src/services/organisation/organisation.enum';
import { getRemovePipelineErrorMessage, getRemoveStagePipelineErrorMessage } from 'src/services/deal-flow/deal-flow.utils';
import { useGetPipelineQuery, useGetAllPipelinesQuery, useRemovePipelineMutation, useRemovePipelineStageMutation } from 'src/services/deal-flow/deal-flow.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import EmptyContent from 'src/components/empty-content';
import { ConfirmDialog } from 'src/components/custom-dialog';
import ListSkeleton from 'src/components/list/list-skeleton';

import PipelineEditorModal from '../modal/pipeline-editor-modal';

const DEFAULT_PAGE_STATE: PageOptions = {
  page: 1,
  per_page: 50,
}

export default function DealFLowPipelineSettingsCard() {

  const { t } = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const authContext = useAuthContext();

  const pipelineRemoveConfirm = useBoolean();
  const stageRemoveConfirm = useBoolean();

  const [pageState, setPageState] = useState<PageOptions>(DEFAULT_PAGE_STATE);
  const [selectedPipelineId, setSelectedPipelineId] = useState<string>('');
  const [currentPipelineId, setCurrentPipelineId] = useState<string | null>(null);
  const [currentStageId, setCurrentStageId] = useState<string | null>(null);

  const pipelineEditorControl = useBoolean();

  const [removePipelineStage] = useRemovePipelineStageMutation();
  const [removePipeline] = useRemovePipelineMutation();

  const { currentData: pipelinesData, isFetching: isLoadingPipelines } = useGetAllPipelinesQuery({
    page: pageState.page,
    perPage: pageState.per_page,
  });

  const pipelines = useMemo(() => pipelinesData?.results || [], [pipelinesData]);

  const { data: selectedPipeline, isLoading: isLoadingPipeline } = useGetPipelineQuery(
    { pipelineId: selectedPipelineId as string },
    {
      skip: !selectedPipelineId
    }
  );

  // auto-select first pipeline if none selected and pipelines are available
  React.useEffect(() => {
    if (pipelines.length > 0 && !selectedPipelineId) {
      setSelectedPipelineId(pipelines[0].id);
    }
  }, [pipelines, selectedPipelineId]);

  const handleAddPipeline = () => {
    setCurrentPipelineId(null);
    pipelineEditorControl.onTrue();
  };

  const handleEditPipeline = (pipelineId: string) => {
    setCurrentPipelineId(pipelineId);
    pipelineEditorControl.onTrue();
  };

  const handleClosePipelineEditor = () => {
    setCurrentPipelineId(null);
    pipelineEditorControl.onFalse();
  };

  const handleRemoveStage = async (stageId: string) => {
    try {
      await removePipelineStage({
        pipelineId: selectedPipelineId,
        stageId
      }).unwrap();

      enqueueSnackbar(t('admin-organization.settings.deal_flow_pipeline.api.remove_stage.success'), { variant: 'success' });
      stageRemoveConfirm.onFalse();
    } catch (error) {

      const errMsg = getRemoveStagePipelineErrorMessage(error);
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  };

  const handleRemovePipeline = async () => {
    if (!selectedPipelineId) return;

    try {
      await removePipeline({
        pipelineId: selectedPipelineId
      }).unwrap();

      pipelineRemoveConfirm.onFalse();
      enqueueSnackbar(t('admin-organization.settings.deal_flow_pipeline.api.remove_pipeline.success'), { variant: 'success' });
      setSelectedPipelineId('');

    } catch (error) {
      const errMsg = getRemovePipelineErrorMessage(error);
      enqueueSnackbar(errMsg, { variant: 'error' });
    }
  };

  const canEditSettings = useMemo(() => {
    if (!authContext?.account) return false;
    return [AccountRole.Admin, AccountRole.Op_Manager].includes(authContext.account.org_role);
  }, [authContext])

  const renderStagesTable = () => {
    if (!selectedPipeline) {
      return (
        <MuiBox sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexGrow: 1,
          p: 3
        }}>
          <EmptyContent
            filled
            title={t('admin-organization.settings.deal_flow_pipeline.no_pipelines')}
            description={t('admin-organization.settings.deal_flow_pipeline.create_pipeline_first')}
            action={
              <Button
                color="primary"
                variant="contained"
                disabled={!canEditSettings}
                startIcon={<Iconify icon="lucide:plus" />}
                onClick={handleAddPipeline}
                sx={{ mt: 3 }}
              >
                {t('admin-organization.settings.deal_flow_pipeline.create_pipeline')}
              </Button>
            }
            sx={{ py: 8 }}
          />
        </MuiBox>
      );
    }

    const stages = selectedPipeline.stages || [];

    if (stages.length === 0) {
      return (
        <MuiBox sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {t('admin-organization.settings.deal_flow_pipeline.no_stages')}
          </Typography>
        </MuiBox>
      );
    }

    return (
      <TableContainer sx={{ overflow: 'unset' }}>
        <Scrollbar>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell>{t('admin-organization.settings.deal_flow_pipeline.table.stage_name')}</TableCell>
                <TableCell>{t('admin-organization.settings.deal_flow_pipeline.table.order')}</TableCell>
                <TableCell>{t('admin-organization.settings.deal_flow_pipeline.table.type')}</TableCell>
                <TableCell>{t('admin-organization.settings.deal_flow_pipeline.table.completion_rate')}</TableCell>
                <TableCell>{t('admin-organization.settings.deal_flow_pipeline.table.color')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stages
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((stage) => (
                  <TableRow key={stage.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" textTransform="capitalize">
                        {stage.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {stage.order}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" textTransform="capitalize">
                        {stage.type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {stage.completion_rate}%
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {stage.color && (
                          <MuiBox
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              backgroundColor: stage.color,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary">
                          {stage.color || t('common.not_set')}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        disabled={!canEditSettings}
                        onClick={() => {
                          setCurrentStageId(stage.id);
                          stageRemoveConfirm.onTrue();
                        }}
                      >
                        <Iconify icon="mingcute:delete-2-line" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={!canEditSettings}
                        onClick={() => handleEditPipeline(selectedPipeline.id)}
                      >
                        <Iconify icon="solar:pen-bold" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Scrollbar>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card
        sx={{
          width: '100%',
          position: 'relative',
          boxShadow: 0
        }}
      >
        <CardHeader
          avatar={
            <Iconify
              icon="garden:workflow-26"
              sx={{
                width: 30,
                height: 30,
                color: theme.palette.primary.main,
              }}
            />
          }
          title={
            <Typography variant="h6" gutterBottom>
              {t('admin-organization.settings.deal_flow_pipeline.title')}
            </Typography>
          }
          action={
            <Button
              variant="contained"
              startIcon={<Iconify icon="mingcute:add-line" />}
              onClick={handleAddPipeline}
              disabled={!canEditSettings}
            >
              {t('admin-organization.settings.deal_flow_pipeline.add_pipeline')}
            </Button>
          }
        />

        <CardContent sx={{ mt: 2 }}>
          <Stack spacing={3}>
            {/* Pipeline Selector */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Typography variant="subtitle2" sx={{ minWidth: 'fit-content' }}>
                {t('admin-organization.settings.deal_flow_pipeline.select_pipeline')}:
              </Typography>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{t('admin-organization.settings.deal_flow_pipeline.pipeline')}</InputLabel>
                <Select
                  value={selectedPipelineId}
                  label={t('admin-organization.settings.deal_flow_pipeline.pipeline')}
                  onChange={(e) => setSelectedPipelineId(e.target.value)}
                  disabled={isLoadingPipelines || pipelines.length === 0}
                >
                  {pipelines.map((pipeline) => (
                    <MenuItem key={pipeline.id} value={pipeline.id}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2">
                          {pipeline.name}
                        </Typography>
                        <Chip
                          label={pipeline.currency?.code || 'N/A'}
                          size="small"
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedPipeline && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!canEditSettings}
                  startIcon={<Iconify icon="solar:pen-bold" />}
                  onClick={() => handleEditPipeline(selectedPipeline.id)}
                >
                  {t('common.edit')}
                </Button>
              )}

              {selectedPipeline && (
                <Button
                  variant="outlined"
                  color="error"
                  disabled={!canEditSettings}
                  size="small"
                  startIcon={<Iconify icon="mingcute:delete-2-line" />}
                  onClick={pipelineRemoveConfirm.onTrue}
                >
                  {t('common.remove')}
                </Button>
              )}
              
              {selectedPipeline && selectedPipeline.auto_archive_enabled && (
                <Chip
                  label={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Iconify icon="mdi:archive-clock-outline" width={16} />
                      <Typography variant="caption">
                        {t('admin-organization.settings.deal_flow_pipeline.auto_archive_settings')}:
                        {selectedPipeline.auto_archive_in_days
                          ? ` ${selectedPipeline.auto_archive_in_days} ${t('common.time.days')}`
                          : t('common.enabled')}
                      </Typography>
                    </Stack>
                  }
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Stack>

            {/* Stages Table */}
            <MuiBox>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>
                {t('admin-organization.settings.deal_flow_pipeline.stage_configuration')}
              </Typography>

              {
                isLoadingPipelines && (
                  <MuiBox sx={{ p: 3, textAlign: 'center' }}>
                    <ListSkeleton />
                  </MuiBox>
                )
              }

              {
                !isLoadingPipelines && (
                  <>
                    {renderStagesTable()}
                  </>
                )
              }

            </MuiBox>
          </Stack>
        </CardContent>
      </Card>

      {/* Pipeline Editor Modal */}
      <PipelineEditorModal
        open={pipelineEditorControl.value}
        onClose={handleClosePipelineEditor}
        pipelineId={currentPipelineId}
      />

      {/* Pipeline remove confirmation dialog */}
      <ConfirmDialog
        open={pipelineRemoveConfirm.value}
        onClose={pipelineRemoveConfirm.onFalse}
        title={t('admin-organization.settings.deal_flow_pipeline.remove_pipeline_title')}
        content={t('admin-organization.settings.deal_flow_pipeline.remove_pipeline_confirm')}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={handleRemovePipeline}
          >
            {t('common.remove')}
          </Button>
        }
      />

      {/* Stage remove confirmation dialog */}
      <ConfirmDialog
        open={stageRemoveConfirm.value}
        onClose={() => {
          stageRemoveConfirm.onFalse();
          setCurrentStageId(null);
        }}
        title={t('admin-organization.settings.deal_flow_pipeline.remove_stage_title')}
        content={t('admin-organization.settings.deal_flow_pipeline.remove_stage_confirm')}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => handleRemoveStage(currentStageId as string)}
          >
            {t('common.remove')}
          </Button>
        }
      />
    </Box>


  )
}